import React, { useEffect, useRef, useState } from 'react';
import { BookOpen, FileText, ExternalLink } from 'lucide-react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';

const Publications = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const sectionRef = useRef(null);

  useEffect(() => {
    const currentRef = sectionRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  const publications = [
    {
      type: 'book',
      title: 'Qurānic and Biblical Narrative Discourse: A Contemporary Approach',
      authors: 'Afsar, A.',
      year: '2018',
      publisher: 'IRI, IIUI, Islamabad',
      category: 'Books'
    },
    {
      type: 'book',
      title: 'Designing Culturally Appropriate Material',
      authors: 'Afsar, A.',
      year: '2018',
      publisher: 'Dawah Academy, IIUI, Islamabad',
      category: 'Books'
    },
    {
      type: 'journal',
      title: 'The pronunciation constraints of syllable stress-coloration in Pakistani English',
      authors: 'Afsar, A.',
      year: '2024',
      journal: 'Journal of Humanities, Social and Management Sciences',
      volume: '5(1)',
      pages: '21-35',
      category: 'Journal Articles'
    },
    {
      type: 'journal',
      title: 'The Social Relevance of Literature: A Critique of Mohsin Hamid\u2019s Moth Smoke, Kamila Shamsie\u2019s Salt and Saffron, and Sara Suleri\u2019s Boys will be boys',
      authors: 'Afsar, A.',
      year: '2023',
      journal: 'Journal of Policy Research',
      volume: '9(2)',
      pages: '162-167',
      category: 'Journal Articles'
    },
    {
      type: 'journal',
      title: 'In Search of Ideographs: Exploring the Rhetorical Landscape of Inaugural Speeches of Head of State/Government in Pakistan (1947-2018)',
      authors: 'Afsar, A.',
      year: '2021',
      journal: 'Global Language Review',
      volume: '6(3)',
      pages: '10-23',
      category: 'Journal Articles'
    },
    {
      type: 'journal',
      title: 'Situational Characteristics of Pakistani University Student English: Variation across Registers, Sub-registers, and Disciplines',
      authors: 'Afsar, A.',
      year: '2021',
      journal: 'Kashmir Journal of Language Research',
      volume: '24(2)',
      pages: '75-111',
      category: 'Journal Articles'
    },
    {
      type: 'journal',
      title: 'A Corpus Stylistic analysis of Speech and Thought Presentation in James Joyce\u2019s Dubliners',
      authors: 'Afsar, A.',
      year: '2019',
      journal: 'International Journal of English Linguistics',
      volume: '10(1)',
      pages: '277-292',
      category: 'Journal Articles'
    },
    {
      type: 'journal',
      title: 'Cohesion in Surat al-Fatiha: A Linguistic Study',
      authors: 'Afsar, A.',
      year: '2019',
      journal: 'Kashmir Journal of Language Research',
      volume: '22(2)',
      pages: '1-20',
      category: 'Journal Articles'
    },
    {
      type: 'journal',
      title: 'Magical Realism, Social Protest and Anti-Colonial Sentiments in One Hundred Years of Solitude',
      authors: 'Afsar, A.',
      year: '2014',
      journal: 'Asian Journal of Latin American Studies',
      volume: '27(2)',
      pages: '1-26',
      category: 'Journal Articles'
    },
    {
      type: 'journal',
      title: 'From the Word of Allah to the Words of Men: The Quran and the Poetics of Translation',
      authors: 'Afsar, A.',
      year: '2013',
      journal: 'Islamic Studies',
      volume: '51(2)',
      pages: '193-211',
      category: 'Journal Articles'
    },
    {
      type: 'journal',
      title: 'Jonah/Yunus Story in the Bible and the Quran: A Narrative Study',
      authors: 'Afsar, A.',
      year: '2009',
      journal: 'Islamic Studies',
      volume: '48',
      pages: '319-339',
      category: 'Journal Articles'
    },
    {
      type: 'journal',
      title: 'A Comparative Study of the Intended Sacrifice of Isaac/Ishmael in the Bible and the Quran',
      authors: 'Afsar, A.',
      year: '2007',
      journal: 'Islamic Studies',
      volume: '46(4)',
      pages: '483-498',
      category: 'Journal Articles'
    },
    {
      type: 'journal',
      title: 'Plot Motifs in Joseph/Yusuf Story: A Comparative Study of Biblical and Qurānic Narrative',
      authors: 'Afsar, A.',
      year: '2006',
      journal: 'Islamic Studies',
      volume: '45(2)',
      pages: '167-189',
      category: 'Journal Articles'
    },
    {
      type: 'journal',
      title: 'A Literary Critical Approach to Quranic Parables',
      authors: 'Afsar, A.',
      year: '2005',
      journal: 'Islamic Studies',
      volume: '44(4)',
      pages: '481-501',
      category: 'Journal Articles'
    }
  ];

  const categories = ['all', 'Books', 'Journal Articles'];

  const filteredPublications = activeCategory === 'all' 
    ? publications 
    : publications.filter(pub => pub.category === activeCategory);

  const getCategoryIcon = (type) => {
    return type === 'book' ? BookOpen : FileText;
  };

  return (
    <section id="publications" ref={sectionRef} className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-3">
            Publications & Research
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-cyan-500 mx-auto mb-4"></div>
          <p className="text-gray-600 max-w-2xl mx-auto text-sm">
            Selected publications in applied linguistics, corpus studies, and comparative narrative discourse
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`px-5 py-1.5 rounded-full font-medium text-sm transition-all duration-300 ${
                activeCategory === category
                  ? 'bg-blue-600 text-white shadow-lg scale-105'
                  : 'bg-gray-100 text-gray-700 hover:bg-blue-50 hover:text-blue-600'
              }`}
            >
              {category === 'all' ? 'All Publications' : category}
            </button>
          ))}
        </div>

        {/* Publications List */}
        <div className="max-w-5xl mx-auto space-y-4">
          {filteredPublications.map((pub) => {
            const Icon = getCategoryIcon(pub.type);
            return (
              <Card
                key={`${pub.title}-${pub.year}`}
                className={`p-5 border-l-4 border-blue-600 hover:shadow-xl transition-all duration-500 group ${
                  isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
                }`}
              >
                <div className="flex items-start gap-3">
                  {/* Icon */}
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-blue-600 transition-colors duration-300">
                    <Icon className="text-blue-600 group-hover:text-white transition-colors duration-300" size={20} />
                  </div>

                  {/* Content */}
                  <div className="flex-grow">
                    {/* Title */}
                    <h3 className="text-base font-bold text-gray-900 mb-1.5 group-hover:text-blue-600 transition-colors leading-snug">
                      {pub.title}
                    </h3>

                    {/* Authors & Year */}
                    <p className="text-sm text-gray-700 mb-1.5">
                      <span className="font-medium">{pub.authors}</span>
                      <span className="text-gray-400 mx-1.5">•</span>
                      <span className="text-gray-600">{pub.year}</span>
                    </p>

                    {/* Publication Details */}
                    <div className="text-sm text-gray-600">
                      {pub.type === 'book' && (
                        <p>{pub.publisher}</p>
                      )}
                      {pub.type === 'journal' && (
                        <p>
                          <span className="italic">{pub.journal}</span>, {pub.volume}, {pub.pages}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-12 max-w-4xl mx-auto">
          <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl">
            <div className="text-3xl font-bold text-blue-600 mb-1">40+</div>
            <div className="text-sm text-gray-700">Published Articles</div>
          </div>
          <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl">
            <div className="text-3xl font-bold text-blue-600 mb-1">2</div>
            <div className="text-sm text-gray-700">Books Authored</div>
          </div>
          <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl">
            <div className="text-3xl font-bold text-blue-600 mb-1">22</div>
            <div className="text-sm text-gray-700">PhD Students Supervised</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Publications;